import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import BlockDisplayComponent from "../BlockDisplayComponent/BlockDisplayComponent";
import {
  RiAddLine,
  RiCloseLine,
  RiDeleteBinLine,
  RiSearchLine,
  RiUserAddLine,
  RiUserUnfollowLine,
  RiTeamLine,
  RiSaveLine,
} from "react-icons/ri";
import "./AddAllGuests.scss";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";

const GROOM_COLLECTION = "GroomGuests";
const BRIDE_COLLECTION = "BrideGuests";

/**
 * ACCESS RULE:
 * - Trial users: allowed
 * - Premium users: allowed ONLY if addon is added
 *
 * Update only these helpers if your Firestore key names differ.
 */
const isTrialUser = (data) => {
  return (
    data?.plan === "trial" ||
    data?.subscriptionPlan === "trial" ||
    data?.packageType === "trial" ||
    data?.isTrial === true
  );
};

const isPremiumUser = (data) => {
  return (
    data?.plan === "premium" ||
    data?.subscriptionPlan === "premium" ||
    data?.packageType === "premium" ||
    data?.isPremium === true
  );
};

const hasTableAddon = (data) => {
  return (
    data?.addons?.seatingArrangement === true
  );
};

const canAccessTableAssignments = (data) => {
  if (isTrialUser(data)) return true;
  if (isPremiumUser(data)) return hasTableAddon(data);
  return false;
};

const AddAllGuests = () => {
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  const [selectedList, setSelectedList] = useState("");
  const [weddingData, setWeddingData] = useState(null);
  const [weddingLoading, setWeddingLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  const [confirmedGuests, setConfirmedGuests] = useState([]);
  const [tables, setTables] = useState([]);
  const [globalUsedTableOrders, setGlobalUsedTableOrders] = useState([]);

  const [loadingGuests, setLoadingGuests] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const [loadingGlobalOrders, setLoadingGlobalOrders] = useState(false);

  const [creatingTable, setCreatingTable] = useState(false);
  const [deletingTableId, setDeletingTableId] = useState("");
  const [savingGuestKey, setSavingGuestKey] = useState("");
  const [savingGuestAction, setSavingGuestAction] = useState("");
  const [renamingTable, setRenamingTable] = useState(false);

  const [tableSearch, setTableSearch] = useState({});
  const [activeTableLabel, setActiveTableLabel] = useState("");
  const [tableNumberInput, setTableNumberInput] = useState("");
  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);
        const slug = weddingData?.slug || "";

        if (!slug) {
          if (isMounted) setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", slug));

        if (!isMounted) return;

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading site theme:", error);

        if (isMounted) {
          setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
        }
      } finally {
        if (isMounted) {
          setThemeLoading(false);
        }
      }
    };

    if (!weddingLoading) {
      loadTheme();
    }

    return () => {
      isMounted = false;
    };
  }, [weddingData, weddingLoading]);

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  const getGuestKey = (guest) => `${guest.docId}-${guest.guestIndex}`;
  const getTableLabel = (order) => `Table ${order}`;

  const getTablesCollectionRef = (uid, listName) =>
    collection(db, "users", uid, "tableBoards", listName, "tables");

  const extractTableOrder = (seatNumber) => {
    const value = (seatNumber || "").trim();
    const match = value.match(/^Table\s+(\d+)$/i);
    return match ? Number(match[1]) : null;
  };

  useEffect(() => {
    const fetchWeddingData = async () => {
      try {
        if (!currentUser) {
          setWeddingLoading(false);
          return;
        }

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setWeddingData(data);

          const allowed = canAccessTableAssignments(data);
          setHasAccess(allowed);

          if (allowed) {
            setSelectedList(GROOM_COLLECTION);
          } else {
            setSelectedList("");
            setConfirmedGuests([]);
            setTables([]);
            setGlobalUsedTableOrders([]);
          }
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error("Error fetching wedding data:", error);
        setHasAccess(false);
      } finally {
        setWeddingLoading(false);
      }
    };

    fetchWeddingData();
  }, [currentUser]);

  const siteSlug = weddingData?.slug || "";

  const syncFamilyToPublicSite = async ({
    familyDocId,
    collectionName,
    updatedGuests,
    familyData,
  }) => {
    if (!siteSlug || !collectionName || !familyDocId) return;

    const publicRef = doc(db, "sites", siteSlug, collectionName, familyDocId);
    const publicSnap = await getDoc(publicRef);

    if (publicSnap.exists()) {
      await updateDoc(publicRef, { guests: updatedGuests });
      return;
    }

    await setDoc(publicRef, {
      familyName: familyData?.familyName || "",
      guestType: familyData?.guestType || "",
      guests: updatedGuests,
      createdBy: familyData?.createdBy || currentUser?.email || "unknown",
      createdAt: familyData?.createdAt || null,
    });
  };

  const fetchConfirmedGuests = async (collectionName) => {
    try {
      if (!currentUser || !collectionName || !hasAccess) return;

      setLoadingGuests(true);

      const snapshot = await getDocs(
        collection(db, "users", currentUser.uid, collectionName)
      );

      const guests = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const docId = docSnap.id;

        if (Array.isArray(data.guests)) {
          data.guests.forEach((guest, idx) => {
            if (guest.status === "Participation Confirmed") {
              guests.push({
                name: guest.name || "Unnamed Guest",
                docId,
                guestIndex: idx,
                seatNumber: guest.seatNumber || "",
                familyName: data.familyName || "",
                status: guest.status || "",
              });
            }
          });
        }
      });

      guests.sort((a, b) => a.name.localeCompare(b.name));
      setConfirmedGuests(guests);
    } catch (err) {
      console.error(`Error fetching confirmed guests from ${collectionName}:`, err);
      setConfirmedGuests([]);
    } finally {
      setLoadingGuests(false);
    }
  };

  const fetchTables = async (collectionName) => {
    try {
      if (!currentUser || !collectionName || !hasAccess) return;

      setLoadingTables(true);

      const snapshot = await getDocs(
        getTablesCollectionRef(currentUser.uid, collectionName)
      );

      const loadedTables = snapshot.docs.map((tableDoc) => {
        const data = tableDoc.data();
        return {
          id: tableDoc.id,
          order: Number(data.order) || 0,
          createdAt: data.createdAt || 0,
        };
      });

      loadedTables.sort((a, b) => a.order - b.order);
      setTables(loadedTables);
    } catch (err) {
      console.error("Error fetching tables:", err);
      setTables([]);
    } finally {
      setLoadingTables(false);
    }
  };

  const fetchGlobalTableOrders = async () => {
    try {
      if (!currentUser || !hasAccess) return;

      setLoadingGlobalOrders(true);

      const usedOrders = new Set();
      const listNames = [GROOM_COLLECTION, BRIDE_COLLECTION];

      for (const listName of listNames) {
        const tableSnap = await getDocs(
          getTablesCollectionRef(currentUser.uid, listName)
        );

        tableSnap.forEach((docSnap) => {
          const order = Number(docSnap.data()?.order);
          if (Number.isInteger(order) && order > 0) {
            usedOrders.add(order);
          }
        });

        const guestSnap = await getDocs(
          collection(db, "users", currentUser.uid, listName)
        );

        guestSnap.forEach((docSnap) => {
          const data = docSnap.data();
          if (!Array.isArray(data.guests)) return;

          data.guests.forEach((guest) => {
            const order = extractTableOrder(guest?.seatNumber);
            if (Number.isInteger(order) && order > 0) {
              usedOrders.add(order);
            }
          });
        });
      }

      setGlobalUsedTableOrders(Array.from(usedOrders).sort((a, b) => a - b));
    } catch (error) {
      console.error("Error fetching global table orders:", error);
      setGlobalUsedTableOrders([]);
    } finally {
      setLoadingGlobalOrders(false);
    }
  };

  useEffect(() => {
    if (!selectedList || !currentUser || !hasAccess) return;

    fetchConfirmedGuests(selectedList);
    fetchTables(selectedList);
    fetchGlobalTableOrders();
    setTableSearch({});
    setActiveTableLabel("");
    setTableNumberInput("");
  }, [selectedList, currentUser, hasAccess]);

  const assignedGuests = useMemo(() => {
    return confirmedGuests.filter(
      (guest) => guest.seatNumber && guest.seatNumber.trim() !== ""
    );
  }, [confirmedGuests]);

  const totalConfirmed = confirmedGuests.length;
  const totalAssigned = assignedGuests.length;
  const totalLeft = totalConfirmed - totalAssigned;

  const derivedTablesFromAssignments = useMemo(() => {
    const assignedLabels = Array.from(
      new Set(
        confirmedGuests
          .map((guest) => (guest.seatNumber || "").trim())
          .filter(Boolean)
      )
    );

    const assignedOrders = assignedLabels
      .map((label) => extractTableOrder(label))
      .filter((value) => value !== null);

    return assignedOrders
      .filter((order) => !tables.some((table) => table.order === order))
      .map((order) => ({
        id: `derived-${order}`,
        order,
        derived: true,
      }));
  }, [confirmedGuests, tables]);

  const allTables = useMemo(() => {
    const merged = [...tables, ...derivedTablesFromAssignments];
    merged.sort((a, b) => a.order - b.order);
    return merged;
  }, [tables, derivedTablesFromAssignments]);

  const nextTableOrder = useMemo(() => {
    if (globalUsedTableOrders.length === 0) return 1;
    return Math.max(...globalUsedTableOrders) + 1;
  }, [globalUsedTableOrders]);

  const guestsByTable = useMemo(() => {
    const grouped = {};

    allTables.forEach((table) => {
      grouped[getTableLabel(table.order)] = [];
    });

    confirmedGuests.forEach((guest) => {
      const tableLabel = (guest.seatNumber || "").trim();
      if (tableLabel) {
        if (!grouped[tableLabel]) grouped[tableLabel] = [];
        grouped[tableLabel].push(guest);
      }
    });

    Object.keys(grouped).forEach((tableLabel) => {
      grouped[tableLabel].sort((a, b) => a.name.localeCompare(b.name));
    });

    return grouped;
  }, [allTables, confirmedGuests]);

  const unassignedGuests = useMemo(() => {
    return confirmedGuests.filter(
      (guest) => !guest.seatNumber || guest.seatNumber.trim() === ""
    );
  }, [confirmedGuests]);

  const getSearchResultsForTable = (tableLabel) => {
    const term = (tableSearch[tableLabel] || "").trim().toLowerCase();

    const pool = unassignedGuests.filter((guest) =>
      guest.name.toLowerCase().includes(term)
    );

    if (!term) return pool.slice(0, 8);
    return pool.slice(0, 10);
  };

  const refreshCurrentView = async () => {
    if (!selectedList) return;

    await Promise.all([
      fetchConfirmedGuests(selectedList),
      fetchTables(selectedList),
      fetchGlobalTableOrders(),
    ]);
  };

  const createTable = async () => {
    try {
      if (!currentUser || !selectedList || !hasAccess) return;

      setCreatingTable(true);

      const newOrder = nextTableOrder;
      const existsAlready = globalUsedTableOrders.includes(newOrder);

      if (existsAlready) {
        alert("That table number already exists.");
        return;
      }

      await addDoc(getTablesCollectionRef(currentUser.uid, selectedList), {
        order: newOrder,
        createdAt: Date.now(),
      });

      await refreshCurrentView();

      const newLabel = getTableLabel(newOrder);
      setActiveTableLabel(newLabel);
      setTableNumberInput(String(newOrder));
      setTableSearch((prev) => ({ ...prev, [newLabel]: "" }));
    } catch (err) {
      console.error("Error creating table:", err);
      alert("Failed to create a new table.");
    } finally {
      setCreatingTable(false);
    }
  };

  const assignGuestToTable = async (guest, tableLabel) => {
    try {
      if (!currentUser || !hasAccess) return;

      const guestKey = getGuestKey(guest);
      setSavingGuestKey(guestKey);
      setSavingGuestAction("add");

      const familyRef = doc(db, "users", currentUser.uid, selectedList, guest.docId);
      const familySnap = await getDoc(familyRef);
      const familyData = familySnap.data();

      if (!familyData || !Array.isArray(familyData.guests)) return;

      const updatedGuests = [...familyData.guests];
      updatedGuests[guest.guestIndex] = {
        ...updatedGuests[guest.guestIndex],
        seatNumber: tableLabel,
      };

      await updateDoc(familyRef, { guests: updatedGuests });

      await syncFamilyToPublicSite({
        familyDocId: guest.docId,
        collectionName: selectedList,
        updatedGuests,
        familyData,
      });

      await refreshCurrentView();

      setTableSearch((prev) => ({ ...prev, [tableLabel]: "" }));
    } catch (err) {
      console.error("Error assigning guest to table:", err);
      alert("Failed to assign guest to table.");
    } finally {
      setSavingGuestKey("");
      setSavingGuestAction("");
    }
  };

  const removeGuestFromTable = async (guest) => {
    try {
      if (!currentUser || !hasAccess) return;

      const guestKey = getGuestKey(guest);
      setSavingGuestKey(guestKey);
      setSavingGuestAction("remove");

      const familyRef = doc(db, "users", currentUser.uid, selectedList, guest.docId);
      const familySnap = await getDoc(familyRef);
      const familyData = familySnap.data();

      if (!familyData || !Array.isArray(familyData.guests)) return;

      const updatedGuests = [...familyData.guests];
      updatedGuests[guest.guestIndex] = {
        ...updatedGuests[guest.guestIndex],
        seatNumber: "",
      };

      await updateDoc(familyRef, { guests: updatedGuests });

      await syncFamilyToPublicSite({
        familyDocId: guest.docId,
        collectionName: selectedList,
        updatedGuests,
        familyData,
      });

      await refreshCurrentView();
    } catch (err) {
      console.error("Error removing guest from table:", err);
      alert("Failed to remove guest from table.");
    } finally {
      setSavingGuestKey("");
      setSavingGuestAction("");
    }
  };

  const deleteTable = async (table) => {
    const tableLabel = getTableLabel(table.order);

    if (table.derived) {
      alert("This table still has assigned guests. Remove them first.");
      return;
    }

    const guestsInside = guestsByTable[tableLabel] || [];
    if (guestsInside.length > 0) {
      alert("Please remove all guests from this table before deleting it.");
      return;
    }

    try {
      if (!currentUser || !hasAccess) return;

      setDeletingTableId(table.id);

      await deleteDoc(
        doc(
          db,
          "users",
          currentUser.uid,
          "tableBoards",
          selectedList,
          "tables",
          table.id
        )
      );

      if (activeTableLabel === tableLabel) {
        setActiveTableLabel("");
        setTableNumberInput("");
      }

      await refreshCurrentView();
    } catch (err) {
      console.error("Error deleting table:", err);
      alert("Failed to delete table.");
    } finally {
      setDeletingTableId("");
    }
  };

  const renameTable = async () => {
    try {
      if (!currentUser || !selectedList || !activeTableLabel || !hasAccess) return;

      const activeTable = allTables.find(
        (table) => getTableLabel(table.order) === activeTableLabel
      );

      if (!activeTable) return;

      const currentOrder = activeTable.order;
      const newOrder = Number(tableNumberInput);

      if (!Number.isInteger(newOrder) || newOrder <= 0) {
        alert("Please enter a valid table number.");
        return;
      }

      if (newOrder !== currentOrder && globalUsedTableOrders.includes(newOrder)) {
        alert("That table number already exists. Please use a different number.");
        return;
      }

      if (newOrder === currentOrder) {
        closeTableModal();
        return;
      }

      setRenamingTable(true);

      const oldLabel = getTableLabel(currentOrder);
      const newLabel = getTableLabel(newOrder);

      if (!activeTable.derived) {
        await updateDoc(
          doc(
            db,
            "users",
            currentUser.uid,
            "tableBoards",
            selectedList,
            "tables",
            activeTable.id
          ),
          {
            order: newOrder,
          }
        );
      }

      const familySnapshot = await getDocs(
        collection(db, "users", currentUser.uid, selectedList)
      );

      for (const familyDoc of familySnapshot.docs) {
        const familyData = familyDoc.data();

        if (!Array.isArray(familyData.guests)) continue;

        let changed = false;
        const updatedGuests = familyData.guests.map((guest) => {
          if ((guest.seatNumber || "").trim() === oldLabel) {
            changed = true;
            return {
              ...guest,
              seatNumber: newLabel,
            };
          }
          return guest;
        });

        if (changed) {
          await updateDoc(
            doc(db, "users", currentUser.uid, selectedList, familyDoc.id),
            { guests: updatedGuests }
          );

          await syncFamilyToPublicSite({
            familyDocId: familyDoc.id,
            collectionName: selectedList,
            updatedGuests,
            familyData,
          });
        }
      }

      await refreshCurrentView();
      setActiveTableLabel(newLabel);
      setTableNumberInput(String(newOrder));
    } catch (error) {
      console.error("Error renaming table:", error);
      alert("Failed to update the table number.");
    } finally {
      setRenamingTable(false);
    }
  };

  const openTableModal = (tableLabel) => {
    if (!hasAccess) return;

    setActiveTableLabel(tableLabel);
    setTableSearch((prev) => ({ ...prev, [tableLabel]: prev[tableLabel] || "" }));

    const order = extractTableOrder(tableLabel);
    setTableNumberInput(order ? String(order) : "");
  };

  const closeTableModal = () => {
    setActiveTableLabel("");
    setTableNumberInput("");
  };

  if (weddingLoading) {
    return <div className="assignTables__empty">Loading wedding data...</div>;
  }

  if (!currentUser) {
    return <div className="assignTables__empty">Please log in first.</div>;
  }

  if (!hasAccess) {
    return (
      <BlockDisplayComponent
        title="This feature is locked"
        description="Premium users can access it only when this add-on has been added to their package. Call 070 536 7980 to get this feature"
        primaryButtonText="Add this feature"
        secondaryButtonText="Go Back"
        onPrimaryClick={() => navigate("/premium")}
        onSecondaryClick={() => navigate(-1)}
      />
    );
  }

  const brideName = weddingData?.bride_name || "Bride";
  const groomName = weddingData?.groom_name || "Groom";

  const groomCollectionName = GROOM_COLLECTION;
  const brideCollectionName = BRIDE_COLLECTION;

  const title =
    selectedList === groomCollectionName
      ? `${groomName}'s Table Assignments`
      : `${brideName}'s Table Assignments`;

  const isLoading = loadingGuests || loadingTables || loadingGlobalOrders;

  const activeTable = allTables.find(
    (table) => getTableLabel(table.order) === activeTableLabel
  );

  const activeGuests = activeTableLabel ? guestsByTable[activeTableLabel] || [] : [];
  const activeSearchResults = activeTableLabel
    ? getSearchResultsForTable(activeTableLabel)
    : [];

  if (themeLoading) {
    return null;
  }

  if (weddingLoading) {
    return <div className="assignTables__empty">Loading wedding data...</div>;
  }
  return (
    <section className="assignTables" style={themeStyle}>
      <div className="assignTables__hero">
        <div>
          <p className="assignTables__eyebrow">Seating Planner</p>
          <h1 className="assignTables__title">{title}</h1>
        </div>
      </div>

      <div className="assignTables__tabs" role="tablist" aria-label="Guest source">
        <ButtonComponent
          size="sm"
          type="button"
          role="tab"
          aria-selected={selectedList === groomCollectionName}
          className={`assignTables__tab ${selectedList === groomCollectionName ? "is-active" : ""
            }`}
          onClick={() => setSelectedList(groomCollectionName)}
        >
          {groomName}’s List
        </ButtonComponent>

        <ButtonComponent
          size="sm"
          type="button"
          role="tab"
          aria-selected={selectedList === brideCollectionName}
          className={`assignTables__tab ${selectedList === brideCollectionName ? "is-active" : ""
            }`}
          onClick={() => setSelectedList(brideCollectionName)}
        >
          {brideName}’s List
        </ButtonComponent>
      </div>

      <div className="assignTables__summaryGrid">
        <div className="assignTables__summaryCard">
          <div className="assignTables__summaryIcon">
            <RiUserAddLine />
          </div>
          <div>
            <p className="assignTables__summaryLabel">Confirmed Guests</p>
            <h3 className="assignTables__summaryValue">{totalConfirmed}</h3>
          </div>
        </div>

        <div className="assignTables__summaryCard">
          <div className="assignTables__summaryIcon">
            <RiUserAddLine />
          </div>
          <div>
            <p className="assignTables__summaryLabel">Assigned Guests</p>
            <h3 className="assignTables__summaryValue">{totalAssigned}</h3>
          </div>
        </div>

        <div className="assignTables__summaryCard">
          <div className="assignTables__summaryIcon">
            <RiUserUnfollowLine />
          </div>
          <div>
            <p className="assignTables__summaryLabel">Guests Left to Assign</p>
            <h3 className="assignTables__summaryValue">{totalLeft}</h3>
          </div>
        </div>

        <div className="assignTables__summaryCard">
          <div className="assignTables__summaryIcon">
            <RiAddLine />
          </div>
          <div>
            <p className="assignTables__summaryLabel">Tables</p>
            <h3 className="assignTables__summaryValue">{allTables.length}</h3>
          </div>
        </div>
      </div>

      <div className="assignTables__panel">
        {isLoading ? (
          <div className="assignTables__empty">Loading table assignments…</div>
        ) : totalConfirmed === 0 ? (
          <div className="assignTables__empty">
            No one has confirmed participation yet.
          </div>
        ) : allTables.length === 0 ? (
          <>
            <div className="assignTables__empty">
              No tables added yet. Use <strong>Add New Table</strong> to begin.
            </div>

            <div className="assignTables__bottomActions">
              <ButtonComponent
                as="button"
                className="assignTables__primaryBtn assignTables__primaryBtn--bottom"
                onClick={createTable}
                disabled={creatingTable || !selectedList}
              >
                <RiAddLine />
                <span>{creatingTable ? "Adding..." : "Add New Table"}</span>
              </ButtonComponent>
            </div>
          </>
        ) : (
          <>
            <div className="assignTables__tableGrid">
              {allTables.map((table) => {
                const tableLabel = getTableLabel(table.order);
                const tableGuests = guestsByTable[tableLabel] || [];

                return (
                  <article
                    className="assignTables__tableCard"
                    key={table.id}
                    onClick={() => openTableModal(tableLabel)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openTableModal(tableLabel);
                      }
                    }}
                  >
                    <div className="assignTables__tableCardTop">
                      <div>
                        <h3 className="assignTables__tableTitle">{tableLabel}</h3>
                      </div>

                      <div className="assignTables__tableActions">
                        <span className="assignTables__guestCountBadge">
                          {tableGuests.length} guest{tableGuests.length !== 1 ? "s" : ""}
                        </span>

                        <ButtonComponent
                          as="button"
                          className="assignTables__iconBtn is-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTable(table);
                          }}
                          disabled={deletingTableId === table.id}
                        >
                          <RiDeleteBinLine />
                        </ButtonComponent>
                      </div>
                    </div>

                    <div className="assignTables__tablePreview">
                      {tableGuests.length === 0 ? (
                        <div className="assignTables__assignedEmpty is-compact">
                          No guests assigned yet.
                        </div>
                      ) : (
                        <div className="assignTables__previewLine">
                          {tableGuests.map((guest) => guest.name).join(", ")}
                        </div>
                      )}
                    </div>

                    <div className="assignTables__cardFooter">
                      <ButtonComponent
                        as="button"
                        className="assignTables__openBtn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTableModal(tableLabel);
                        }}
                      >
                        <RiTeamLine />
                        <span>Open Table</span>
                      </ButtonComponent>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="assignTables__bottomActions">
              <ButtonComponent
                as="button"
                className="assignTables__primaryBtn assignTables__primaryBtn--bottom"
                onClick={createTable}
                disabled={creatingTable || !selectedList}
              >
                <RiAddLine />
                <span>{creatingTable ? "Adding..." : "Add New Table"}</span>
              </ButtonComponent>
            </div>
          </>
        )}
      </div>

      {activeTable && (
        <div className="assignTables__modalBackdrop" onClick={closeTableModal}>
          <div
            className="assignTables__modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={activeTableLabel}
          >
            <div className="assignTables__modalHead">
              <div>
                <p className="assignTables__tableEyebrow">Manage Table</p>
                <h3 className="assignTables__modalTitle">{activeTableLabel}</h3>
              </div>

              <ButtonComponent
                as="button"
                className="assignTables__iconBtn"
                onClick={closeTableModal}
              >
                <RiCloseLine />
              </ButtonComponent>
            </div>

            <div className="assignTables__modalStats">
              <span className="assignTables__guestCountBadge">
                {activeGuests.length} guest{activeGuests.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="assignTables__tableNumberEditor">
              <label className="assignTables__tableNumberLabel">
                Edit table number
              </label>

              <div className="assignTables__tableNumberRow">
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="assignTables__tableNumberInput"
                  value={tableNumberInput}
                  onChange={(e) => setTableNumberInput(e.target.value)}
                  placeholder="Enter table number"
                />

                <ButtonComponent
                  as="button"
                  className="assignTables__saveNumberBtn"
                  onClick={renameTable}
                  disabled={renamingTable}
                >
                  <RiSaveLine />
                  <span>{renamingTable ? "Saving..." : "Save Number"}</span>
                </ButtonComponent>
              </div>

              <p className="assignTables__tableNumberHint">
                Table numbers are shared across both groom and bride lists. Duplicate
                numbers are not allowed.
              </p>
            </div>

            <div className="assignTables__modalBody">
              <div className="assignTables__modalSection">
                <div className="assignTables__searchWrap">
                  <span className="assignTables__searchIcon">
                    <RiSearchLine />
                  </span>
                  <input
                    type="text"
                    className="assignTables__searchInput"
                    placeholder="Search confirmed guests to add..."
                    value={tableSearch[activeTableLabel] || ""}
                    onChange={(e) =>
                      setTableSearch((prev) => ({
                        ...prev,
                        [activeTableLabel]: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="assignTables__results is-modal">
                  {activeSearchResults.length === 0 ? (
                    <div className="assignTables__resultsEmpty">
                      {unassignedGuests.length === 0
                        ? "All confirmed guests are already assigned."
                        : "No matching unassigned guests found."}
                    </div>
                  ) : (
                    activeSearchResults.map((guest) => {
                      const guestKey = getGuestKey(guest);
                      const isSaving = savingGuestKey === guestKey;
                      const isAdding = isSaving && savingGuestAction === "add";

                      return (
                        <button
                          key={guestKey}
                          type="button"
                          className="assignTables__resultItem"
                          onClick={() => assignGuestToTable(guest, activeTableLabel)}
                          disabled={isSaving}
                        >
                          <div className="assignTables__guestInfo">
                            <span className="assignTables__guestName">{guest.name}</span>
                            <span className="assignTables__guestSub">
                              {guest.familyName || "Confirmed guest"}
                            </span>
                          </div>
                          <span className="assignTables__addPill">
                            {isAdding ? "Adding..." : "Add"}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="assignTables__modalSection">
                <div className="assignTables__assignedList">
                  {activeGuests.length === 0 ? (
                    <div className="assignTables__assignedEmpty">
                      No guests assigned to this table yet.
                    </div>
                  ) : (
                    activeGuests.map((guest) => {
                      const guestKey = getGuestKey(guest);
                      const isSaving = savingGuestKey === guestKey;
                      const isRemoving = isSaving && savingGuestAction === "remove";

                      return (
                        <div className="assignTables__assignedItem" key={guestKey}>
                          <div className="assignTables__guestInfo">
                            <span className="assignTables__guestName">{guest.name}</span>
                            <span className="assignTables__guestSub">
                              {guest.familyName || "Confirmed guest"}
                            </span>
                          </div>

                          <ButtonComponent
                            as="button"
                            className="assignTables__removeBtn"
                            onClick={() => removeGuestFromTable(guest)}
                            disabled={isSaving}
                          >
                            <RiCloseLine />
                            <span>{isRemoving ? "Removing..." : "Remove"}</span>
                          </ButtonComponent>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AddAllGuests;